import { Component, OnInit } from '@angular/core'
import { FormGroup, FormControl, Validators } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { ModalController } from '@ionic/angular'
import { combineLatest, map, Subject, takeUntil } from 'rxjs'
import { AppSettingsRepository } from '../../../repositories/app-settings.repository'
import { AppSettingService } from '../../../services/app-setting.service'
import { Translation } from '../../../services/translation.service'
import { SidebarPosition } from '../efp-sidebar-container/efp-sidebar-container.component'
import { PrivacyPolicyModalComponent } from '../privacy-policy-modal/privacy-policy-modal.component'
import { Doka360ProjectSelectorService } from '../../../services/doka360-project-selector.service'
import { DeviceModeRepository } from '../../../repositories/device-mode.repository'

@Component({
  selector: 'efp-general-settings',
  templateUrl: './general-settings.component.html',
})
export class GeneralSettingsComponent implements OnInit {
  private readonly pageLeave$ = new Subject<void>()

  public isNative = this.deviceModeRepository.isMobileSignal

  protected readonly SidebarPosition = SidebarPosition

  public appLanguages: Readonly<string[]> = []

  public readonly settingsForm = new FormGroup({
    language: new FormControl('', { validators: Validators.required, nonNullable: true }),
    showSyncIndicator: new FormControl(false, {
      validators: Validators.required,
      nonNullable: true,
    }),
  })

  public country$ = combineLatest([
    this.appSettingsRepository.appSettings$,
    this.doka360ProjectSelectorService.doka360Project$,
  ]).pipe(
    map(([appSettings, doka360Project]) =>
      doka360Project !== undefined ? doka360Project.country : appSettings?.country
    )
  )

  public isDoka360Scope = false

  constructor(
    private readonly translateService: Translation,
    private readonly appSettingsService: AppSettingService,
    public readonly appSettingsRepository: AppSettingsRepository,
    public readonly activatedRoute: ActivatedRoute,
    private readonly modalCtrl: ModalController,
    private readonly doka360ProjectSelectorService: Doka360ProjectSelectorService,
    private readonly deviceModeRepository: DeviceModeRepository
  ) {
    this.doka360ProjectSelectorService.currentDoka360ProjectUid$
      .pipe(takeUntil(this.pageLeave$))
      .subscribe((currentDoka360ProjectUid) => {
        this.isDoka360Scope = !!currentDoka360ProjectUid
      })
  }

  async ngOnInit(): Promise<void> {
    this.appLanguages = this.appSettingsService.appLanguages

    // appSettings
    const appSettings = await this.appSettingsRepository.getAppSettings()

    this.settingsForm.patchValue(
      {
        language: appSettings.language,
        showSyncIndicator: appSettings.showSyncIndicator,
      },
      { emitEvent: false }
    )

    this.registerFormValueChanges()
  }

  private registerFormValueChanges(): void {
    this.settingsForm.controls.language.valueChanges
      .pipe(takeUntil(this.pageLeave$))
      .subscribe((language) => void this.changeLanguage(language))
    this.settingsForm.controls.showSyncIndicator.valueChanges
      .pipe(takeUntil(this.pageLeave$))
      .subscribe(() => void this.changeShowSyncIndicator())
  }

  private async changeLanguage(language: string): Promise<void> {
    await this.translateService.changeLanguage(language)
    await this.appSettingsRepository.updateLanguage(language)
  }

  public toggleShowSync(): void {
    this.settingsForm.controls.showSyncIndicator.patchValue(
      !this.settingsForm.controls.showSyncIndicator.value
    )
  }

  private async changeShowSyncIndicator(): Promise<void> {
    await this.appSettingsRepository.updateShowSync(
      this.settingsForm.controls.showSyncIndicator.value
    )
  }

  public async presentPrivacyPolicyModal(): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: PrivacyPolicyModalComponent,
      backdropDismiss: false,
      cssClass: 'soft-edges-modal',
      componentProps: {
        canDismiss: true,
      },
    })
    await modal.present()
    await modal.onDidDismiss()
  }

  public async toggleNativeMode(): Promise<void> {
    await this.deviceModeRepository.setIsMobileMode(!this.isNative())
  }
}
